import { type ReactNode } from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTabs,
  CardTitle,
} from '@/components/Card'
import styles from './ResponsesCard.module.css'
import { TabBar, TabItem } from '@/components/TabBar'
import { type ResponsesInformationTab } from '@/features/jobs/details/DetailsViewModel'

export interface ResponseProps {
  label: ReactNode
  children: ReactNode
}

export function Response({ label, children }: ResponseProps) {
  return (
    <div className={styles.responseItem}>
      <div className={styles.responseItemLabel}>{label}</div>
      <div className={styles.responseItemValue}>{children}</div>
    </div>
  )
}

export interface QuestionsTabProps {
  children: ReactNode
}

export function QuestionsTab({ children }: QuestionsTabProps) {
  return <div className={styles.questionTabRootContainer}>{children}</div>
}

export interface ResponsesCardProps {
  activeTab: ResponsesInformationTab | undefined
  questionCount: number
  showNotesTab: boolean
  showPersonalTab: boolean
  showBusinessTab: boolean
  showRefundTab: boolean
  onTabChange: (tab: ResponsesInformationTab) => unknown
  children: ReactNode
  personalInfoTabTestId?: string
  businessTabTestId?: string
  refundTabTestId?: string
}

export function ResponsesCard({
  activeTab,
  questionCount,
  showNotesTab,
  showPersonalTab,
  showBusinessTab,
  showRefundTab,
  onTabChange,
  children,
  personalInfoTabTestId,
  businessTabTestId,
  refundTabTestId,
}: ResponsesCardProps) {
  return (
    <Card unconstrained>
      <CardHeader compact bordered>
        <CardTitle cardLayout>Onboarding Information</CardTitle>

        <CardTabs>
          <TabBar>
            <TabItem
              cardLayout
              active={activeTab === 'Questions'}
              onPress={() => onTabChange('Questions')}
            >
              Questions ({questionCount})
            </TabItem>

            {showNotesTab && (
              <TabItem
                active={activeTab === 'Notes'}
                onPress={() => onTabChange('Notes')}
              >
                Notes
              </TabItem>
            )}

            {showPersonalTab && (
              <TabItem
                cardLayout
                active={activeTab === 'PersonalInfo'}
                onPress={() => onTabChange('PersonalInfo')}
                testId={personalInfoTabTestId}
              >
                Personal
              </TabItem>
            )}

            {showBusinessTab && (
              <TabItem
                cardLayout
                active={activeTab === 'BusinessInfo'}
                onPress={() => onTabChange('BusinessInfo')}
                testId={businessTabTestId}
              >
                Business
              </TabItem>
            )}

            {showRefundTab && (
              <TabItem
                cardLayout
                active={activeTab === 'RefundInfo'}
                onPress={() => onTabChange('RefundInfo')}
                testId={refundTabTestId}
              >
                Refund
              </TabItem>
            )}
          </TabBar>
        </CardTabs>
      </CardHeader>
      <CardContent scroll listContent>
        {children}
      </CardContent>
    </Card>
  )
}
